import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Save, Globe, FileText, Search, Plus, Trash2, RefreshCw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const DEFAULT_PAGES = [
  { path: "/", label: "Начало" },
  { path: "/info", label: "Информация" },
  { path: "/about", label: "За нас" },
  { path: "/pricing", label: "Цени" },
  { path: "/blog", label: "Блог" },
  { path: "/contact", label: "Контакт" },
  { path: "/faq", label: "FAQ" },
  { path: "/legal", label: "Правна информация" },
  { path: "/guide", label: "Ръководство" },
  { path: "/register", label: "Регистрация" },
  { path: "/login", label: "Вход" },
];

export default function SeoManager({ API }) {
  const { isDark } = useTheme();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [globalSettings, setGlobalSettings] = useState({ siteName: "", siteUrl: "", ogImage: "", author: "", defaultKeywords: "" });

  const fetchSeo = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/seo`);
      setPages(data.pages || []);
      setGlobalSettings(data.global || { siteName: "Nutramilo", siteUrl: "https://nutramilo.com", ogImage: "/app-icon.png", author: "", defaultKeywords: "" });
    } catch { toast.error("Грешка при зареждане на SEO"); }
    finally { setLoading(false); }
  }, [API]);

  useEffect(() => { fetchSeo(); }, [fetchSeo]);

  const updatePage = (idx, field, value) => {
    setPages(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const savePage = async (idx) => {
    setSaving(idx);
    try {
      await axios.put(`${API}/admin/seo/page`, pages[idx]);
      toast.success(`SEO за ${pages[idx].path} запазено!`);
    } catch { toast.error("Грешка при запазване"); }
    finally { setSaving(null); }
  };

  const saveGlobal = async () => {
    setSaving("global");
    try {
      await axios.put(`${API}/admin/seo/global`, globalSettings);
      toast.success("Глобални SEO настройки запазени!");
    } catch { toast.error("Грешка"); }
    finally { setSaving(null); }
  };

  const addCustomPage = () => {
    setPages(prev => [...prev, { path: "/new-page", title: "", description: "", keywords: "", ogTitle: "", ogDescription: "" }]);
  };

  const removePage = async (idx) => {
    const p = pages[idx];
    if (!window.confirm(`Изтриване на SEO за ${p.path}?`)) return;
    try {
      await axios.delete(`${API}/admin/seo/page`, { data: { path: p.path } });
      setPages(prev => prev.filter((_, i) => i !== idx));
      toast.success("Изтрито");
    } catch { toast.error("Грешка"); }
  };

  const syncConfig = async () => {
    setSaving("sync");
    try {
      await axios.post(`${API}/admin/seo/sync-config`);
      toast.success("seo-config.json синхронизиран!");
    } catch { toast.error("Грешка"); }
    finally { setSaving(null); }
  };

  const inputCls = `text-sm ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200'}`;
  const labelCls = `text-xs font-medium mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`;

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#0EA5E9]" /></div>;

  return (
    <div className="space-y-4" data-testid="seo-manager">
      {/* Global Settings */}
      <Card className={isDark ? 'bg-gray-800 border-gray-700' : 'card-elevated border-0'}>
        <CardHeader className="pb-2">
          <CardTitle className={`text-sm font-semibold flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
            <Globe className="w-4 h-4 text-[#0EA5E9]" /> Глобални SEO настройки
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Име на сайта</label><Input value={globalSettings.siteName} onChange={e => setGlobalSettings(p => ({...p, siteName: e.target.value}))} className={inputCls} /></div>
            <div><label className={labelCls}>URL на сайта</label><Input value={globalSettings.siteUrl} onChange={e => setGlobalSettings(p => ({...p, siteUrl: e.target.value}))} className={inputCls} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>OG Image път</label><Input value={globalSettings.ogImage} onChange={e => setGlobalSettings(p => ({...p, ogImage: e.target.value}))} className={inputCls} /></div>
            <div><label className={labelCls}>Автор</label><Input value={globalSettings.author} onChange={e => setGlobalSettings(p => ({...p, author: e.target.value}))} className={inputCls} /></div>
          </div>
          <div><label className={labelCls}>Ключови думи (по подразбиране)</label><Input value={globalSettings.defaultKeywords} onChange={e => setGlobalSettings(p => ({...p, defaultKeywords: e.target.value}))} className={inputCls} /></div>
          <div className="flex gap-2">
            <Button size="sm" onClick={saveGlobal} disabled={saving === "global"} className="gradient-primary text-white">
              {saving === "global" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Запази глобални
            </Button>
            <Button size="sm" variant="outline" onClick={syncConfig} disabled={saving === "sync"}>
              {saving === "sync" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />} Синхронизирай с config
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Per-page SEO */}
      <div className="flex items-center justify-between">
        <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : ''}`}>
          <FileText className="w-4 h-4 inline mr-1 text-[#6366F1]" /> SEO по страници ({pages.length})
        </h3>
        <Button size="sm" variant="outline" onClick={addCustomPage}><Plus className="w-3 h-3 mr-1" /> Добави страница</Button>
      </div>

      {pages.map((p, idx) => {
        const isDefault = DEFAULT_PAGES.some(dp => dp.path === p.path);
        return (
          <Card key={p.path + idx} className={isDark ? 'bg-gray-800 border-gray-700' : 'card-elevated border-0'}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-mono px-2 py-0.5 rounded ${isDark ? 'bg-[#0EA5E9]/20 text-[#0EA5E9]' : 'bg-blue-50 text-blue-600'}`}>{p.path}</span>
                  {isDefault && <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>({DEFAULT_PAGES.find(dp => dp.path === p.path)?.label})</span>}
                  <span className={`text-[10px] ${(p.title?.length || 0) > 60 ? 'text-red-500' : isDark ? 'text-gray-500' : 'text-gray-400'}`}>Title: {p.title?.length || 0}/60</span>
                  <span className={`text-[10px] ${(p.description?.length || 0) > 160 ? 'text-red-500' : isDark ? 'text-gray-500' : 'text-gray-400'}`}>Desc: {p.description?.length || 0}/160</span>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => savePage(idx)} disabled={saving === idx}
                    className={saving === idx ? '' : 'text-[#10B981]'}>
                    {saving === idx ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  </Button>
                  {!isDefault && <Button size="sm" variant="ghost" onClick={() => removePage(idx)} className="text-red-500"><Trash2 className="w-3 h-3" /></Button>}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {!isDefault && <div><label className={labelCls}>Път (URL)</label><Input value={p.path} onChange={e => updatePage(idx, "path", e.target.value)} className={inputCls} /></div>}
                <div><label className={labelCls}>Title (заглавие за Google)</label><Input value={p.title || ""} onChange={e => updatePage(idx, "title", e.target.value)} className={inputCls} placeholder="Nutramilo — ..." data-testid={`seo-title-${p.path}`} /></div>
                <div><label className={labelCls}>Description (описание за Google)</label><textarea value={p.description || ""} onChange={e => updatePage(idx, "description", e.target.value)} className={`w-full px-3 py-2 rounded-md border resize-none h-16 ${inputCls}`} placeholder="До 160 символа..." data-testid={`seo-desc-${p.path}`} /></div>
                <div><label className={labelCls}>Keywords (ключови думи)</label><Input value={p.keywords || ""} onChange={e => updatePage(idx, "keywords", e.target.value)} className={inputCls} placeholder="AI, хранене, метаболизъм..." /></div>
              </div>
              {/* Google Preview */}
              <div className={`p-2 rounded-lg border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                <p className="text-xs text-[#1a0dab] font-medium truncate">{p.title || "Без заглавие"}</p>
                <p className="text-[11px] text-[#006621] truncate">{globalSettings.siteUrl}{p.path}</p>
                <p className={`text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-600'} line-clamp-2`}>{p.description || "Без описание"}</p>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
